// src/pages/AdminAssignments.jsx
import { useEffect, useMemo, useState } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import { Input } from "../components/Input";
import {
  adminListUsers,
  listTrainings,
  adminAssignTraining,
  adminUnassignTraining,
} from "../services/api";
import { loadSession } from "../auth";
import { useTranslation } from "react-i18next";

function UserRow({ u, checked, onToggle }) {
  return (
    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-black/5 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={() => onToggle(u)} />
      <div className="flex-1">
        <div className="font-medium text-sm">{u.name || "—"}</div>
        <div className="text-xs text-black/60">{u.email}</div>
      </div>
      {u?.derived?.isManager && (
        <span className="px-2 py-0.5 rounded-full text-xs border bg-black/5 border-black/10">
          manager
        </span>
      )}
    </label>
  );
}

function TrainingRow({ t, selected, onSelect }) {
  return (
    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-black/5 cursor-pointer">
      <input
        type="radio"
        name="training"
        checked={selected}
        onChange={() => onSelect(t)}
      />
      <div className="flex-1">
        <div className="font-medium text-sm">{t.title || t.slug}</div>
        {t.description && (
          <div className="text-xs text-black/60">{t.description}</div>
        )}
      </div>
      {t.visibility === false && (
        <span className="px-2 py-0.5 rounded-full text-xs border bg-yellow-50 border-yellow-200 text-yellow-800">
          hidden
        </span>
      )}
    </label>
  );
}

export default function AdminAssignments() {
  const me = loadSession()?.user;

  const { t } = useTranslation();

  const isOrgAdmin =
    me?.derived?.isOrgAdmin ||
    (Array.isArray(me?.roles) && me.roles.includes("org_super_admin"));

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Users
  const [users, setUsers] = useState([]);
  const [uq, setUq] = useState("");
  const [onlyManagers, setOnlyManagers] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Trainings
  const [trainings, setTrainings] = useState([]);
  const [tq, setTq] = useState("");
  const [selectedTraining, setSelectedTraining] = useState(null);

  // Actions
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [forceUnassign, setForceUnassign] = useState(false);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const [{ items: userItems }, { items: trainingItems }] = await Promise.all([
        adminListUsers({
          q: "",
          page: 1,
          pageSize: 100,
          isManager: "",
          includeDeactivated: false,
        }),
        listTrainings({
          q: "",
          page: 1,
          pageSize: 100,
        }),
      ]);
      setUsers(userItems || []);
      setTrainings(trainingItems || []);
    } catch (e) {
      setErr(e?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isOrgAdmin) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgAdmin]);

  const filteredUsers = useMemo(() => {
    const term = uq.trim().toLowerCase();
    let list = users;
    if (term) {
      list = list.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(term) ||
          (u.email || "").toLowerCase().includes(term)
      );
    }
    if (onlyManagers) {
      if (onlyManagers === "true") list = list.filter((u) => !!u?.derived?.isManager);
      if (onlyManagers === "false") list = list.filter((u) => !u?.derived?.isManager);
    }
    return list;
  }, [users, uq, onlyManagers]);

  const filteredTrainings = useMemo(() => {
    const term = tq.trim().toLowerCase();
    let list = trainings;
    if (term) {
      list = list.filter(
        (t) =>
          (t.title || "").toLowerCase().includes(term) ||
          (t.slug || "").toLowerCase().includes(term)
      );
    }
    return list;
  }, [trainings, tq]);

  function toggleUser(u) {
    setSelectedUserIds((prev) =>
      prev.includes(u.id) ? prev.filter((id) => id !== u.id) : [...prev, u.id]
    );
  }

  async function handleAssign() {
    if (!selectedTraining) {
      alert("Please select a training");
      return;
    }
    if (!selectedUserIds.length) {
      alert("Please select at least one user");
      return;
    }
    setSubmitting(true);
    setSuccessMsg("");
    try {
      await adminAssignTraining({
        trainingSlug: selectedTraining.slug,
        trainingId: selectedTraining.id,
        assigneeUserIds: selectedUserIds,
        status: "NOT_STARTED",
      });
      setSuccessMsg(
        `Assigned "${selectedTraining.title || selectedTraining.slug}" to ${selectedUserIds.length} user(s).`
      );
    } catch (e) {
      alert(e?.message || "Failed to assign training");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUnassign() {
    if (!selectedTraining) {
      alert("Please select a training to unassign");
      return;
    }
    if (!selectedUserIds.length) {
      alert("Please select at least one user");
      return;
    }
    setSubmitting(true);
    setSuccessMsg("");
    try {
      await adminUnassignTraining({
        trainingSlug: selectedTraining.slug,
        trainingId: selectedTraining.id,
        assigneeUserIds: selectedUserIds,
        force: !!forceUnassign,
      });
      setSuccessMsg(
        `Unassigned "${selectedTraining.title || selectedTraining.slug}" from ${selectedUserIds.length} user(s).`
      );
    } catch (e) {
      alert(e?.message || "Failed to unassign training");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOrgAdmin) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-black/70">
            {t('youneedsuperorgadmin')}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <h1 className="text-2xl font-bold">{t("assignments")}</h1>
        <div className="flex gap-2">
          <Button onClick={load} variant="ghost">
            Refresh
          </Button>
        </div>
      </div>

      {successMsg && (
        <div className="p-3 rounded-xl border border-green-200 bg-green-50 text-sm text-green-900">
          {successMsg}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Users */}
        <Card title={t("selectUsers")}>
          <div className="flex items-center gap-2 mb-3">
            <Input
              placeholder={t("searchNameOrEmail")}
              value={uq}
              onChange={(e) => setUq(e.target.value)}
            />
            <select
              className="px-3 py-2 rounded-xl border border-black/10 text-sm"
              value={onlyManagers}
              onChange={(e) => setOnlyManagers(e.target.value)}
            >
              <option value="">{t('all')}</option>
              <option value="true">{t('managersonly')}</option>
              <option value="false">{t('nonmanagers')}</option>
            </select>
          </div>

          {loading ? (
            <div className="text-sm text-black/60">{t('loadingUsers')}</div>
          ) : err ? (
            <div className="text-sm text-red-600">{err}</div>
          ) : filteredUsers.length ? (
            <div className="max-h-[420px] overflow-auto space-y-1 pr-1">
              {filteredUsers.map((u) => (
                <UserRow
                  key={u.id}
                  u={u}
                  checked={selectedUserIds.includes(u.id)}
                  onToggle={toggleUser}
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-black/60">{t('noUsersFound')}</div>
          )}

          <div className="mt-3 text-xs text-black/60">
            {t('selected')}: <span className="font-medium">{selectedUserIds.length}</span>
          </div>
        </Card>

        {/* Trainings */}
        <Card title="Select Training">
          <div className="flex items-center gap-2 mb-3">
            <Input
              placeholder={t("searchTrainings")}
              value={tq}
              onChange={(e) => setTq(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="text-sm text-black/60">{t('loadingTrainings')}</div>
          ) : err ? (
            <div className="text-sm text-red-600">{err}</div>
          ) : filteredTrainings.length ? (
            <div className="max-h-[420px] overflow-auto space-y-1 pr-1">
              {filteredTrainings.map((t) => (
                <TrainingRow
                  key={t.id || t.slug}
                  t={t}
                  selected={
                    !!selectedTraining &&
                    (selectedTraining.id === t.id || selectedTraining.slug === t.slug)
                  }
                  onSelect={setSelectedTraining}
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-black/60">{t('noTrainingsFound')}</div>
          )}

          <div className="mt-4 flex items-center gap-4">
            <Button onClick={handleAssign} disabled={submitting}>
              {submitting ? t("assigningPending") : t("assignToSelectedUsers")}
            </Button>
            <Button onClick={handleUnassign} variant="ghost" disabled={submitting}>
              {submitting ? t("unassigningPending") : t("unassignToSelectedUsers")}
            </Button>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={forceUnassign}
                onChange={(e) => setForceUnassign(e.target.checked)}
              />
              {t("forceUnassignTraining")}
            </label>
          </div>
        </Card>
      </div>
    </div>
  );
}
